// The Dance Hall, for visitors: GET /hall loads this. A dollhouse cutaway of
// the hall on the Commons at festival twilight: a stage with velvet curtains
// and the marquee over it, a floor that lights up on the beat, a mirror ball
// scattering specks of light, string lights, a jukebox. The plazmi who are
// here mingle and bop; when they've agreed on a dance (suggest, agree) or a
// crew takes the stage (perform as <crew>), everyone in it dances it together
// in formation, move for move on the shared beat, then bows, to confetti.
// Visitors chat and cheer. The page polls /api/place/hall/live. The light
// outside follows world time, as on a house page (daylight.ts), and the
// hall's own lights (the floor, the bulbs, the mirror ball's specks) are
// stronger after dark.
// When nothing's on, the marquee offers replays of the last twenty (replay.ts; the board over the stage, hallboard.ts).
//   ?still   one frame, no controls (screenshots)     ?time=21  hold the light at that hour
//   ?replay=0  play the newest replay (1 the one before, …) as the page opens
//   ?postcard=<name>  that resident's Hall card: one frame of a dance it was in (kept with the
//            card, /api/house/<name>/hall-postcard), on the stage at dusk, under the postcard paper
import './perf/singlepass.js'; // (P84) first: before any material is made
import * as THREE from 'three';
import { scaled } from './quality.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { camControls } from './camctl.js';
import { setStyle, finishShadows, mat, cyl, sphere, rbox, lathe, at } from './kit.js';
import { makeRenderer, makeStage, makeCamera, island } from './stage.js';
import { makeComposer } from './post.js';
import { fitView } from './fit.js';
import { govern, startPR, watchFrom } from './quality.js';
import { early, stage as loadStage } from './loading.js';
import { arrival, passage, doorSign } from './exit.js';
import { followPage } from './follow.js';
import { wbox } from './house.js';
import { surf } from './surfaces.js';
import * as F from './furniture.js';
import { Fx } from './fx.js';
import { visitor as toyFigure } from './chars.js';
import { FLOOR } from './furnish.js';
import { homeSign, guide } from './door.js';
import { shell } from './ui/shell.js';
import { Talk, isMe } from './ui/talk.js';
import { walkerNow, sayOverMe } from './chatline.js';
import { waysToGo, onTab } from './ui/places.js';
import { Daylight, fixedHourFrom } from './daylight.js';
import { placeWalk, BODY_R } from './walk.js';
import type { IdleGround } from './crowd.js';
import { Troupe, BEAT_MS, floorHouse, injectStyles, letters, mergeStatic, MOVE_EMOJI, showKey, type P, type PlaceLive, type Show, type Showing, type TroupeOpts } from './place.js';
import { Replays, danceWhen } from './replay.js';
import { moveTags } from './movetags.js';
import { hallBoard } from './hallboard.js';
import { soundPage } from './sound/index.js';
import { card as drawCard, noteRegion } from './postcard.js';
import { fit as fitPoints, fitClear } from './frame.js';
import { nameTags } from './nametags.js';

const q = new URLSearchParams(location.search);
// (a postcard from the Hall) whose card this is: the page is its one frame
const cardOf = q.get('postcard');
const still = q.has('still') || !!cardOf;
const ui = document.getElementById('ui') as HTMLElement;
const POLL_MS = 3000;
const W = 18, D = 14, WALL = 6.2, CX = W / 2;
const STAGE = { x0: 3, x1: 15, z0: 0, z1: 4 };
const TILES = { x0: 5, z0: 6, nx: 8, nz: 5 };
// (on a card it hangs lower, over the dancers against the backdrop, rather than across the marquee)
const BALL = new THREE.Vector3(CX, cardOf ? 4.0 : 4.85, 7.9);
const DOOR = [7.4, 10.6];
const RAFTER = 6.35;
const stats = { fps: 0, pr: 0, calls: 0, tris: 0 };
(window as any).__plazmi = stats;
function done() { (window as any).__ready = true; }
const el = (tag: string, cls = '', text = '') => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };

// (a postcard from the Hall) what the card is drawn from (plazmi.http/hall-card-json): the dance as it
// was kept, the resident first of the dancers (eight at most), up to two who watched, each as they looked then
type Look = { slug: string; name?: string; emoji?: string; color?: string; glow?: string };
type HallCard = {
  line?: string | null; show: Showing; dancers: Look[]; watching: Look[];
  postcard: { day: number; address: string; plot?: number; host?: string }; designs?: any[]; frame?: string | null;
};
const CARD_ME = 1.3; // the resident on its card, a little bigger, as on the home card (app.ts POSTCARD_ME)
const CARD_WATCHER = 0.85; // those who watched, a touch smaller than the dancers
const CARD_HOUR = 18.5; // dusk: a lavender sky over the hall, its lights already on
const CARD_LENS = { live: false, bloom: 0.26, tiltBand: 0.42, tiltAmount: 1.4, vignette: 0.2, warmth: 0.08, contrast: 0.06, saturation: 0.08 };
// the moves that read as dancing in one frame, best first: the card holds the dance on the first it has
const SNAP = ['jazz-hands', 'cheer', 'heart', 'pose', 'wave', 'kick', 'hop', 'spin-hop', 'twirl', 'clap', 'shimmy', 'conga', 'dance', 'wiggle', 'sway', 'bob'];

// candy colours for the lights
const PASTELS = ['#ff7eb6', '#ffd36b', '#7ee8c6', '#7ec8ff', '#c59bff', '#ffa36b'].map((c) => new THREE.Color(c));
const MILK = new THREE.Color('#d9cfe8');

// ---------------------------------------------------------------------------
// the room

function wallpaper() {
  const c = document.createElement('canvas'); c.width = c.height = 256;
  const g = c.getContext('2d')!;
  g.fillStyle = '#2e5963'; g.fillRect(0, 0, 256, 256);
  g.fillStyle = 'rgba(255,255,255,0.035)'; for (let x = 0; x < 256; x += 32) g.fillRect(x, 0, 14, 256);
  const star = (x: number, y: number, r: number, col: string) => {
    g.fillStyle = col; g.beginPath();
    for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2, rr = i % 2 ? r * 0.38 : r; g.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); }
    g.fill();
  };
  star(64, 64, 18, '#e9c46a'); star(192, 192, 18, '#e9c46a');
  star(192, 64, 7, '#f4a7c2'); star(64, 192, 7, '#9fe3d0');
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(0.85, 0.85); t.anisotropy = 4;
  return mat('#ffffff', { map: t, rough: 0.92 });
}

function backdrop() {
  // the starry cloth behind the stage
  const c = document.createElement('canvas'); c.width = 512; c.height = 256;
  const g = c.getContext('2d')!;
  const gr = g.createLinearGradient(0, 0, 0, 256); gr.addColorStop(0, '#3a2350'); gr.addColorStop(1, '#5a2a52');
  g.fillStyle = gr; g.fillRect(0, 0, 512, 256);
  for (let i = 0; i < 70; i++) { g.fillStyle = `rgba(255,${220 + (i % 3) * 10},${170 + (i % 5) * 15},${0.35 + (i % 4) * 0.15})`; const x = (i * 97) % 512, y = (i * 53) % 256, r = 1 + (i % 3); g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill(); }
  g.fillStyle = '#ffe9a8'; g.beginPath(); g.arc(400, 70, 26, 0, Math.PI * 2); g.fill();
  g.fillStyle = '#3a2350'; g.beginPath(); g.arc(388, 62, 24, 0, Math.PI * 2); g.fill();
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function room(g: THREE.Group) {
  const paper = wallpaper();
  const wood = mat('#6e4430', { rough: 0.6 }), gold = mat('#d6b05a', { rough: 0.35, metal: 0.6 });
  const plaster = mat('#f3e6cf', { rough: 0.9 });
  g.add(wbox(0, -0.02, 0, W, 0.04, D, surf({ kind: 'planks', color: '#c99862', board: 0.2 })));
  // walls: the back and the left, cut away at the front and right like a dollhouse
  g.add(wbox(-0.35, 0, -0.35, W + 0.35, WALL, 0, [plaster, plaster, plaster, plaster, paper, plaster]));
  g.add(wbox(-0.35, 0, 0, 0, WALL, D + 0.35, [paper, plaster, plaster, plaster, plaster, plaster]));
  for (const [a, b] of [[[0, 0, 0], [W, 1.1, 0.07]], [[0, 0, 0], [0.07, 1.1, D]]] as [number[], number[]][]) {
    g.add(wbox(a[0], a[1], a[2], b[0], b[1], b[2], wood));
  }
  g.add(wbox(0, 1.1, 0, W, 1.17, 0.1, gold), wbox(0, 1.1, 0, 0.1, 1.17, D, gold));
  g.add(wbox(-0.35, WALL, -0.35, W + 0.35, WALL + 0.12, 0.05, gold), wbox(-0.35, WALL, 0, 0.05, WALL + 0.12, D + 0.35, gold));
  // low stubs where the front and right walls were, with a doorway at the front
  for (const [x0, x1] of [[-0.35, DOOR[0]], [DOOR[1], W + 0.35]]) g.add(wbox(x0, 0, D, x1, 0.85, D + 0.35, plaster), wbox(x0, 0.85, D, x1, 0.92, D + 0.35, gold));
  g.add(wbox(W, 0, -0.35, W + 0.35, 0.85, D, plaster), wbox(W, 0.85, -0.35, W + 0.35, 0.92, D, gold));
  // a velvet rope at the door, for the occasion
  for (const x of [DOOR[0] - 0.4, DOOR[1] + 0.4]) { g.add(at(cyl(0.06, 0.1, 0.95, gold, 12), x, 0.48, D + 0.75)); g.add(at(sphere(0.08, gold, 10, 8), x, 1.0, D + 0.75)); }
  // the welcome mat
  g.add(wbox(DOOR[0] + 0.3, 0.02, D - 1.1, DOOR[1] - 0.3, 0.05, D - 0.05, mat('#b8456e', { rough: 1 })));
  // tall windows onto the evening, along the left wall
  const sky = new THREE.MeshBasicMaterial({ map: backdrop(), toneMapped: false });
  for (const z of [4.4, 7.8, 11.2]) {
    const win = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 2.8), sky);
    win.rotation.y = Math.PI / 2; win.position.set(0.04, 3.2, z); g.add(win);
    g.add(wbox(0.02, 1.72, z - 0.92, 0.14, 1.8, z + 0.92, wood), wbox(0.02, 4.6, z - 0.92, 0.14, 4.68, z + 0.92, wood));
    g.add(wbox(0.02, 1.8, z - 0.92, 0.12, 4.6, z - 0.82, wood), wbox(0.02, 1.8, z + 0.82, 0.12, 4.6, z + 0.92, wood), wbox(0.02, 3.15, z - 0.82, 0.1, 3.22, z + 0.82, wood));
    const arch = new THREE.Mesh(new THREE.CircleGeometry(0.82, 24, 0, Math.PI), sky);
    arch.rotation.y = Math.PI / 2; arch.position.set(0.04, 4.6, z); g.add(arch);
  }
}

function stage(g: THREE.Group, line: THREE.Mesh) {
  const { x0, x1, z0, z1 } = STAGE;
  const top = surf({ kind: 'planks', color: '#8a5434', board: 0.16 });
  const skirt = mat('#b8456e', { rough: 0.7 }), gold = mat('#d6b05a', { rough: 0.35, metal: 0.6 });
  g.add(wbox(x0, 0, z0, x1, FLOOR, z1, [skirt, skirt, top, skirt, skirt, skirt]));
  g.add(wbox(x0 - 0.02, FLOOR - 0.06, z1, x1 + 0.02, FLOOR, z1 + 0.05, gold));
  // scallops along the front of the stage
  for (let x = x0 + 0.35; x < x1; x += 0.7) { const s = new THREE.Mesh(new THREE.CircleGeometry(0.3, 16, Math.PI, Math.PI), gold); s.position.set(x, FLOOR - 0.06, z1 + 0.055); g.add(s); }
  g.add(wbox(CX - 1, 0, z1, CX + 1, FLOOR / 2, z1 + 0.55, top));
  // the backdrop and the curtains, drawn open
  const cloth = new THREE.Mesh(new THREE.PlaneGeometry(x1 - x0 - 0.4, 4.6), mat('#ffffff', { map: backdrop(), rough: 1 }));
  cloth.position.set((x0 + x1) / 2, FLOOR + 2.3, 0.08); g.add(cloth);
  const velvet = mat('#a3243b', { rough: 0.55 });
  const drape = (xa: number, xb: number) => {
    const n = Math.round((xb - xa) / 0.28);
    for (let i = 0; i < n; i++) {
      const fold = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.2, 4.7, 10, 1, true, 0, Math.PI), velvet);
      fold.rotation.y = -Math.PI / 2; fold.position.set(xa + 0.14 + i * 0.28, FLOOR + 2.35, 0.32); g.add(fold);
    }
    const tie = rbox(xb - xa + 0.1, 0.1, 0.1, 0.04, gold); tie.position.set((xa + xb) / 2, FLOOR + 1.6, 0.52); g.add(tie);
  };
  drape(x0, x0 + 2.2); drape(x1 - 2.2, x1);
  // the valance: swags along the top
  for (let x = x0 + 0.7; x < x1; x += 1.4) { const s = sphere(0.72, velvet, 16, 10); s.scale.set(1, 0.42, 0.35); s.position.set(x, FLOOR + 4.75, 0.4); g.add(s); }
  g.add(wbox(x0, FLOOR + 4.95, 0, x1, FLOOR + 5.08, 0.62, gold));
  // the marquee over the stage, its words on a canvas the page keeps up to date
  const board = rbox(7.6, 1.15, 0.2, 0.06, mat('#1c1620', { rough: 0.6 }));
  board.position.set(CX, FLOOR + 5.62, 0.16); g.add(board);
  line.userData.keep = true;
  line.position.set(CX, FLOOR + 5.62, 0.27); g.add(line);
  // a vintage microphone, centre stage
  const steel = mat('#3a3a40', { rough: 0.35, metal: 0.7 });
  g.add(at(cyl(0.18, 0.2, 0.03, steel, 16), CX, FLOOR + 0.015, 3.2));
  g.add(at(cyl(0.015, 0.015, 1.35, steel, 8), CX, FLOOR + 0.69, 3.2));
  const mic = sphere(0.075, mat('#c9ccd2', { rough: 0.3, metal: 0.8 }), 12, 10); mic.scale.y = 1.4; mic.position.set(CX, FLOOR + 1.45, 3.2); g.add(mic);
  // speakers either side
  for (const x of [x0 + 0.65, x1 - 0.65]) {
    const sp = rbox(0.9, 1.5, 0.7, 0.06, mat('#2a2228', { rough: 0.8 })); sp.position.set(x, FLOOR + 0.75, 3.3); g.add(sp);
    for (const [y, r] of [[0.42, 0.28], [1.1, 0.16]]) { const c = cyl(r, r, 0.04, mat('#55505a', { rough: 0.5 }), 20); c.rotation.x = Math.PI / 2; c.position.set(x, FLOOR + y, 3.66); g.add(c); }
  }
}

function jukebox() {
  const j = new THREE.Group();
  j.add(rbox(1.1, 1.3, 0.7, 0.08, mat('#8a4b2e', { rough: 0.5 })).translateY(0.65));
  const glowBands = ['#ff7eb6', '#ffd36b', '#7ee8c6', '#7ec8ff'];
  glowBands.forEach((c, i) => {
    const arc = new THREE.Mesh(new THREE.TorusGeometry(0.5 - i * 0.07, 0.03, 8, 24, Math.PI), mat(c, { emissive: c, emissiveIntensity: 2.2 }));
    arc.position.set(0, 1.3, 0.33); arc.userData.noShadow = true; j.add(arc);
  });
  j.add(lathe([[0, 0], [0.55, 0], [0.55, 0.02], [0, 0.02]], mat('#2a2228'), 20).translateY(1.3).rotateX(Math.PI / 2).translateZ(-0.3));
  const grill = rbox(0.8, 0.45, 0.04, 0.03, mat('#d6b05a', { rough: 0.35, metal: 0.6 })); grill.position.set(0, 0.42, 0.35); j.add(grill);
  const win = rbox(0.6, 0.28, 0.04, 0.03, mat('#fff1c8', { emissive: '#ffcf7a', emissiveIntensity: 1.4 })); win.position.set(0, 0.92, 0.35); j.add(win);
  return j;
}

function bar() {
  const b = new THREE.Group();
  b.add(wbox(0, 0, 0, 1.0, 1.05, 3.6, mat('#7a4a2e', { rough: 0.6 })));
  b.add(wbox(-0.05, 1.05, -0.05, 1.1, 1.12, 3.65, mat('#e8d6b4', { rough: 0.4 })));
  const bowl = lathe([[0, 0], [0.12, 0], [0.3, 0.16], [0.32, 0.2], [0, 0.2]], mat('#ffffff', { opacity: 0.5, rough: 0.05 }), 20);
  bowl.position.set(0.55, 1.12, 1.8); b.add(bowl);
  b.add(at(cyl(0.27, 0.12, 0.13, mat('#ff8fb1', { rough: 0.2 }), 20), 0.55, 1.2, 1.8));
  for (let i = 0; i < 5; i++) { const cup = cyl(0.05, 0.04, 0.11, mat(['#ff7eb6', '#7ec8ff', '#ffd36b', '#7ee8c6', '#c59bff'][i], { rough: 0.4 }), 10); cup.position.set(0.5 + (i % 2) * 0.16, 1.18, 0.4 + i * 0.22); b.add(cup); }
  return b;
}

function cafeTable(seed: number) {
  const t = new THREE.Group();
  t.add(F.roundTable({ r: 0.45, cloth: ['#f4d6e2', '#d6ecf4'][seed % 2] } as any));
  for (const a of [0.4, 0.4 + Math.PI]) { const s = F.chair({}); s.position.set(Math.cos(a) * 0.75, 0, Math.sin(a) * 0.75); s.rotation.y = -a - Math.PI / 2; t.add(s); }
  return t;
}

// a soft round glow, for specks of light and pools under the spotlights
function softDot() {
  const c = document.createElement('canvas'); c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.35, 'rgba(255,255,255,0.75)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

// a run of bulbs, sagging between two points; positions only (bulbs are instanced)
function bulbRun(a: number[], b: number[], n: number, sag: number, out: THREE.Vector3[], wire: THREE.Vector3[][]) {
  const line: THREE.Vector3[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const p = new THREE.Vector3(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t - Math.sin(Math.PI * t) * sag, a[2] + (b[2] - a[2]) * t);
    line.push(p);
    if (i > 0 && i < n) out.push(p.clone().add(new THREE.Vector3(0, -0.07, 0)));
  }
  wire.push(line);
}

// ---------------------------------------------------------------------------

async function main() {
  injectStyles();
  const live0: PlaceLive | null = await early<PlaceLive>('/api/place/hall/live');
  const hc: HallCard | null = cardOf
    ? await fetch(`/api/house/${encodeURIComponent(cardOf)}/hall-postcard`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
    : null;
  loadStage(2);
  setStyle('A');
  let vw = innerWidth, vh = innerHeight; const dpr = devicePixelRatio || 1;
  const prCap = still ? Math.min(2, dpr) : Math.min(1.75, dpr);
  let PR = +(q.get('pr') || (still ? prCap : startPR()));
  const renderer = makeRenderer(vw, vh, PR);
  renderer.info.autoReset = false;
  if (!still) { renderer.shadowMap.autoUpdate = false; renderer.shadowMap.needsUpdate = true; }

  const target = new THREE.Vector3(CX, 2.0, 7.0);
  const camDir: [number, number, number] = [0.4, 0.58, 1.0];
  const { scene, sun, hemi } = makeStage(renderer, {
    target, shadowExtent: 15, shadowSize: 2048, sunDir: [-0.3, 1.0, 0.55], fogNear: 120, fogFar: 300,
  });
  // how much the hall's own lights give: more after dark (daylight.ts sets it with the hour)
  let glow = 1;

  const hall = new THREE.Group();
  scene.add(hall);
  hall.add(island(-1.4, -1.4, W + 1.4, D + 3.0, { seed: 9, depth: 1.8, stripes: false, grass: { kind: 'flagstone', color: '#cdbfa8' } }));
  room(hall);
  // the marquee's words, redrawn when the bill changes
  const lineMat = new THREE.MeshBasicMaterial({ transparent: true, toneMapped: false });
  const lineMesh = new THREE.Mesh(new THREE.PlaneGeometry(7.2, 0.95), lineMat);
  let lineKey = '';
  const setLine = (title: string, line: string) => {
    const k = title + line; if (k === lineKey) return; lineKey = k;
    lineMat.map?.dispose();
    lineMat.map = letters([{ text: title.toUpperCase(), size: 34, color: '#ff8fb8' }, { text: line, size: 58, color: '#fff3c8' }], 1024, 136, null);
    lineMat.needsUpdate = true;
  };
  setLine('Tonight', 'an open floor, crews welcome');
  stage(hall, lineMesh);
  hall.add(at(jukebox(), 1.45, 0, 1.2, 0.45));
  hall.add(at(bar(), 0.12, 0, 4.6));
  for (const [x, z] of [[16.3, 6.4], [16.3, 10.0]]) hall.add(at(cafeTable(Math.round(z)), x, 0, z));
  for (const [x, z, k] of [[0.7, 13.2, 'monstera'], [17.3, 1.0, 'monstera'], [2.4, 0.5, 'tree'], [15.6, 0.5, 'tree']] as [number, number, string][]) hall.add(at(F.plant({ kind: k, scale: k === 'tree' ? 1.4 : 1.7, seed: Math.round(x) } as any), x, 0, z));
  // the rafter the mirror ball hangs from
  const beamM = mat('#8a5a3a', { rough: 0.7 });
  hall.add(wbox(-0.2, RAFTER, BALL.z - 0.11, W + 0.2, RAFTER + 0.2, BALL.z + 0.11, beamM));
  hall.add(at(cyl(0.012, 0.012, RAFTER - BALL.y - 0.5, mat('#c9ccd2', { metal: 0.8, rough: 0.3 }), 6), BALL.x, (RAFTER + BALL.y + 0.5) / 2, BALL.z));
  // spotlights on the rafter, aimed at the stage
  const cans: THREE.Group[] = [];
  // (on a card they hang further out, so they never stand over the marquee's words)
  for (const x of [CX - (cardOf ? 4.8 : 4.2), CX + (cardOf ? 4.8 : 4.2)]) {
    const c = new THREE.Group();
    c.add(cyl(0.22, 0.3, 0.55, mat('#2a2228', { rough: 0.5, metal: 0.4 }), 14));
    c.position.set(x, RAFTER - 0.25, BALL.z); c.lookAt(x + (CX - x) * 0.45, FLOOR, 2); c.rotateX(Math.PI / 2);
    hall.add(c); cans.push(c);
  }
  finishShadows(hall);
  mergeStatic(hall);

  // the dance floor: tiles that light up on the beat (one draw)
  const tileGeo = new THREE.BoxGeometry(0.92, 0.05, 0.92);
  const tileMat = new THREE.MeshBasicMaterial({ toneMapped: false });
  const tiles = new THREE.InstancedMesh(tileGeo, tileMat, TILES.nx * TILES.nz);
  const m4 = new THREE.Matrix4();
  for (let i = 0; i < TILES.nx; i++) for (let j = 0; j < TILES.nz; j++) {
    m4.makeTranslation(TILES.x0 + i + 0.5, 0.045, TILES.z0 + j + 0.5);
    tiles.setMatrixAt(i * TILES.nz + j, m4);
    tiles.setColorAt(i * TILES.nz + j, PASTELS[(i + j) % PASTELS.length]);
  }
  tiles.userData.noShadow = true;
  scene.add(tiles);
  scene.add(wbox(TILES.x0 - 0.12, 0.01, TILES.z0 - 0.12, TILES.x0 + TILES.nx + 0.12, 0.035, TILES.z0 + TILES.nz + 0.12, mat('#241c2a', { rough: 0.4 })));
  let pattern = 0;
  const tileColor = new THREE.Color();
  const lightTiles = (beat: number, busy: boolean) => {
    const cx = (TILES.nx - 1) / 2, cz = (TILES.nz - 1) / 2;
    for (let i = 0; i < TILES.nx; i++) for (let j = 0; j < TILES.nz; j++) {
      const r = Math.hypot(i - cx, (j - cz) * 1.3);
      const on = pattern === 0 ? (i + j + beat) % 2 === 0
        : pattern === 1 ? (i + j + beat) % 4 < 2
          : pattern === 2 ? Math.floor(r + beat) % 3 === 0
            : ((i * 7 + j * 13 + beat * 5) % 5) < 2;
      const c = PASTELS[(i + j * 2 + Math.floor(beat / 2)) % PASTELS.length];
      if (on) tileColor.copy(c).multiplyScalar((busy ? 1.75 : 1.3) * (1 + (glow - 1) * 0.4)); else tileColor.copy(MILK).lerp(c, 0.3).multiplyScalar(0.36 * (0.8 + 0.2 * glow));
      tiles.setColorAt(i * TILES.nz + j, tileColor);
    }
    tiles.instanceColor!.needsUpdate = true;
  };

  // string lights round the top of the walls and down from the rafter (two sets that take turns)
  const bulbsAt: THREE.Vector3[] = [], wires: THREE.Vector3[][] = [];
  bulbRun([0.2, WALL - 0.4, 0.25], [W - 0.2, WALL - 0.4, 0.25], 28, 0.35, bulbsAt, wires);
  bulbRun([0.25, WALL - 0.4, 0.2], [0.25, WALL - 0.4, D - 0.2], 22, 0.3, bulbsAt, wires);
  bulbRun([BALL.x, RAFTER, BALL.z], [0.3, WALL - 0.5, D - 0.6], 14, 0.7, bulbsAt, wires);
  bulbRun([BALL.x, RAFTER, BALL.z], [0.3, WALL - 0.5, 0.4], 12, 0.6, bulbsAt, wires);
  bulbRun([BALL.x, RAFTER, BALL.z], [W - 0.3, WALL - 1.3, 0.4], 12, 0.6, bulbsAt, wires);
  const wireM = new THREE.LineBasicMaterial({ color: '#2a2420' });
  for (const w of wires) scene.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(w), wireM));
  const bulbGeo = new THREE.SphereGeometry(0.06, 8, 6);
  const bulbSets = [0, 1].map(() => new THREE.InstancedMesh(bulbGeo, new THREE.MeshBasicMaterial({ toneMapped: false }), Math.ceil(bulbsAt.length / 2)));
  bulbsAt.forEach((p, i) => {
    const set = bulbSets[i % 2], k = Math.floor(i / 2);
    set.setMatrixAt(k, m4.makeTranslation(p.x, p.y, p.z));
    set.setColorAt(k, new THREE.Color(['#ffd9a0', '#ffb3d1', '#b3f0ff', '#d9c2ff'][i % 4]).multiplyScalar(2.2));
  });
  for (const s of bulbSets) { s.count = Math.floor(bulbsAt.length / 2); s.userData.noShadow = true; scene.add(s); }
  const bulbsOn = (k: number, busy: boolean) => bulbSets.forEach((s, i) => { (s.material as THREE.MeshBasicMaterial).color.setScalar((i === k % 2 ? 1 : busy ? 0.25 : 0.55) * glow); });

  // footlights along the front of the stage
  for (let x = STAGE.x0 + 0.7; x < STAGE.x1; x += 1.4) { const b = sphere(0.07, mat('#fff1c8', { emissive: '#ffcf7a', emissiveIntensity: 3 }), 8, 6); b.position.set(x, FLOOR + 0.05, STAGE.z1 - 0.12); b.userData.noShadow = true; scene.add(b); }

  // the mirror ball, and the specks of light it throws on the floor and walls
  const ballMat = new THREE.MeshStandardMaterial({ color: '#e8eef5', metalness: 1, roughness: 0.16, flatShading: true, envMapIntensity: 2.4 });
  const ball = new THREE.Mesh(new THREE.IcosahedronGeometry(0.55, 2), ballMat);
  ball.position.copy(BALL); ball.userData.noShadow = true; scene.add(ball);
  const SPECKS = scaled(96, 'fx', 24);
  const speckDirs: THREE.Vector3[] = [];
  for (let i = 0; i < SPECKS; i++) {
    // a ring of directions below the ball, and a few up toward the walls
    const a = i * 2.39996, el2 = -0.15 - (i % 7) * 0.12 - (i % 3) * 0.04;
    speckDirs.push(new THREE.Vector3(Math.cos(a), el2, Math.sin(a)).normalize());
  }
  const speckMat = new THREE.MeshBasicMaterial({ map: softDot(), color: '#ffffff', transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false });
  const specks = new THREE.InstancedMesh(new THREE.PlaneGeometry(0.3, 0.3), speckMat, SPECKS);
  for (let i = 0; i < SPECKS; i++) specks.setColorAt(i, new THREE.Color(PASTELS[i % PASTELS.length]).lerp(new THREE.Color('#ffffff'), 0.55).multiplyScalar(1.4));
  specks.userData.noShadow = true; specks.frustumCulled = false; scene.add(specks);
  const spin = new THREE.Matrix4(), dir = new THREE.Vector3(), hit = new THREE.Vector3(), quat = new THREE.Quaternion(), scl = new THREE.Vector3();
  const faceUp = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
  const faceZ = new THREE.Quaternion();
  const faceX = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI / 2, 0));
  let ballSpin = 0, ballSpeed = 0.35;
  const throwSpecks = (dt: number) => {
    ballSpin += dt * ballSpeed;
    ball.rotation.y = ballSpin;
    spin.makeRotationY(ballSpin);
    for (let i = 0; i < SPECKS; i++) {
      dir.copy(speckDirs[i]).applyMatrix4(spin);
      // the nearest surface along the ray: the floor (or the stage), the back wall, the left wall
      let best = Infinity, q2 = faceUp;
      const ty = (BALL.y - 0.05) / -dir.y;
      if (dir.y < 0 && ty > 0) {
        hit.copy(BALL).addScaledVector(dir, ty);
        const onStage = hit.x > STAGE.x0 && hit.x < STAGE.x1 && hit.z < STAGE.z1;
        const tt = onStage ? (BALL.y - FLOOR - 0.02) / -dir.y : ty;
        if (tt < best) { best = tt; q2 = faceUp; }
      }
      if (dir.z < 0) { const tz = (BALL.z - 0.03) / -dir.z; if (tz < best) { best = tz; q2 = faceZ; } }
      if (dir.x < 0) { const tx = (BALL.x - 0.03) / -dir.x; if (tx < best) { best = tx; q2 = faceX; } }
      // a speck with nowhere to land (up, or out through the open side) isn't drawn
      if (!Number.isFinite(best)) best = 0;
      hit.copy(BALL).addScaledVector(dir, best);
      const off = best === 0 || hit.x > W + 0.1 || hit.z > D + 0.1 || hit.y > WALL || hit.y < -0.1;
      scl.setScalar(off ? 0 : 0.7 + best * 0.05);
      quat.copy(q2);
      specks.setMatrixAt(i, m4.compose(hit, quat, scl));
    }
    specks.instanceMatrix.needsUpdate = true;
  };

  // beams from the spotlights to the stage
  const beamMat = new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
    uniforms: { col: { value: new THREE.Color('#ffd9a0') }, k: { value: 0.22 } },
    vertexShader: 'varying float vY; void main(){ vY = uv.y; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
    fragmentShader: 'uniform vec3 col; uniform float k; varying float vY; void main(){ float y = clamp(vY, 0.0, 1.0); gl_FragColor = vec4(col * k * y * sqrt(y), 1.0); }',
  });
  const beams: THREE.Mesh[] = [], pools: THREE.Mesh[] = [];
  for (const c of cans) {
    const from = c.position.clone(), to = new THREE.Vector3(from.x + (CX - from.x) * 0.45, FLOOR, 2);
    const len = from.distanceTo(to);
    const geo = new THREE.CylinderGeometry(0.16, 1.5, len, 24, 1, true);
    geo.translate(0, -len / 2, 0);
    const b = new THREE.Mesh(geo, beamMat.clone());
    b.position.copy(from);
    b.quaternion.setFromUnitVectors(new THREE.Vector3(0, -1, 0), to.clone().sub(from).normalize());
    b.userData.noShadow = true; b.renderOrder = 3;
    scene.add(b); beams.push(b);
    const pool = new THREE.Mesh(new THREE.PlaneGeometry(3.4, 3.4), new THREE.MeshBasicMaterial({ map: softDot(), color: '#ffe6b8', transparent: true, opacity: 0.2, blending: THREE.AdditiveBlending, depthWrite: false }));
    pool.rotation.x = -Math.PI / 2; pool.position.set(to.x, FLOOR + 0.015, to.z); scene.add(pool); pools.push(pool);
  }

  // coloured lights that pulse with the music (no shadows)
  const glowL = new THREE.PointLight('#ff8fc4', 30, 15, 1.6); glowL.position.set(CX - 4, 3.4, 9);
  const glowR = new THREE.PointLight('#8fd0ff', 30, 15, 1.6); glowR.position.set(CX + 4, 3.4, 9);
  const stageL = new THREE.PointLight('#ffd9a0', 26, 12, 1.6); stageL.position.set(CX, 3.4, 3.8);
  scene.add(glowL, glowR, stageL);

  // ----- the plazmi here
  const fx = new Fx();
  scene.add(fx.root);
  const things = [
    { id: 'bar', at: [0.62, 6.2], render: 'counter' }, { id: 'bar2', at: [0.62, 7.0], render: 'counter' }, { id: 'juke', at: [1.45, 1.2], render: 'bookshelf' },
    { id: 't1', at: [16.3, 6.4], render: 'dining-set' }, { id: 't2', at: [16.3, 10.0], render: 'dining-set' },
  ] as any[];
  const floor = floorHouse(W, D, [{ id: 'stage', x: STAGE.x0, y: STAGE.z0, w: STAGE.x1 - STAGE.x0, d: STAGE.z1 - STAGE.z0, level: 0 }], things);
  const audienceAt = (i: number, n: number, s: any): P => {
    if (s?.crew) {
      // on the floor in a loose fan, facing the stage
      const a = -0.85 + (1.7 * (i + 0.5)) / Math.max(1, n), r = 4.9 + (i % 2) * 1.0;
      return [CX + Math.sin(a) * r, 4.6 + Math.cos(a) * r];
    }
    // by the door, two staggered rows
    const row = Math.floor(i / 6), k = i % 6, m = Math.min(6, n - row * 6);
    return [CX + (k - (m - 1) / 2) * 1.6 + (row % 2) * 0.8, 12.2 + row * 1.05];
  };
  // whoever's on the stage: the hall's own plazmi, or a replay's cast
  const onStage = (): Troupe => replays?.troupe || troupe;
  // (P89) the hall when nothing's on: little groups round the edges of the floor, looking at the stage
  // or each other, some at the bar or sitting at the tables; the way in from the door and the stage's
  // step kept clear
  const hallIdle: IdleGround = {
    place: 'hall',
    sites: ([[2.6, 9.2], [15.4, 12.4], [2.9, 12.2], [5.2, 12.6], [12.8, 12.6], [3.2, 5.4], [14.6, 4.8], [2.4, 6.6]] as P[])
      .map((p, i) => ({ at: p, view: i === 7 ? [0.4, 6.6] as P : [CX, 2] as P })),
    seats: [6.4, 10.0].flatMap((z, t) => [-1, 1].map((s) => ({ id: `t${t}${s}`, pair: `t${t}`, at: [16.3 + s * 0.85, z] as P, face: [16.3, z] as P, top: 0.475, from: [16.3 + s * 0.85, z + 0.95] as P }))),
    lanes: [{ x0: 6.8, z0: 10.8, x1: 11.2, z1: 14 }, { x0: 7.6, z0: 3.9, x1: 10.4, z1: 5.6 }],
  };
  const hallOpts: TroupeOpts = {
    party: 0.55, idle: hallIdle,
    murmurs: ['♪', 'la la la', 'ooh, this one', 'one, two…', 'hee hee', '♪ ♪', 'what a floor', 'twirl time?', 'mm, punch', 'hm hm hm ♪'],
    start: (i) => [[7.4, 8.2], [10.6, 8.4], [6, 10.4], [12, 10.2], [9, 11.6], [4.2, 12.2], [13.8, 12.3], [7.4, 12.8], [10.6, 12.9], [3, 8], [15, 12]][i % 11] as P,
    formation: (slugs, s) => {
      const n = slugs.length;
      if (s.crew) {
        // the crew on the stage, in a line, facing the hall
        const sp = Math.min(1.55, 10.5 / Math.max(1, n));
        return { spots: slugs.map((_, i) => [CX + (i - (n - 1) / 2) * sp, 2.15] as P), face: [0, 1], stage: true };
      }
      // the dance floor: rows facing the door (the camera's side), the lead in the middle of
      // the front row; up to five make one gently curved line, more make rows, the back ones
      // in the gaps. The spacing grows with the crowd, a body and a half apart at least.
      const rows = n <= 5 ? 1 : n <= 10 ? 2 : 3, per = Math.ceil(n / rows);
      const sp = Math.max(1.45, Math.min(1.9, 8 / Math.max(1, per)));
      const spots: P[] = [];
      for (let i = 0; i < n; i++) {
        const row = Math.floor(i / per), k = i - row * per, inRow = Math.min(per, n - row * per);
        // middle out: 0, then left, right, left…
        const o = k === 0 ? 0 : k % 2 ? (k + 1) / 2 : -k / 2, mean = inRow % 2 ? 0 : -0.5;
        const x = (o - mean - (row % 2 ? 0.5 : 0) * (inRow === per ? 1 : 0)) * sp;
        spots.push([CX + x, 10.5 - row * 1.6 - (rows === 1 ? Math.abs(o) * 0.18 : 0)]);
      }
      return { spots, face: [0, 1] };
    },
    audience: (i, n, s) => audienceAt(i, n, s),
    // the boards, the light-up tiles a little proud of them, the stage and its step
    ground: (x, z) => x > STAGE.x0 && x < STAGE.x1 && z > STAGE.z0 && z < STAGE.z1 ? FLOOR
      : x > CX - 1 && x < CX + 1 && z >= STAGE.z1 && z < STAGE.z1 + 0.55 ? FLOOR / 2
        : x > TILES.x0 && x < TILES.x0 + TILES.nx && z > TILES.z0 && z < TILES.z0 + TILES.nz ? 0.07 : 0.04,
    onBeat: (beat, show) => {
      const busy = !!show && Date.now() >= show.start;
      if (!busy && beat % 16 === 0) pattern = (pattern + 1) % 4;
      if (busy || beat % 2 === 0) lightTiles(busy ? beat : beat / 2, busy);
      bulbsOn(beat, busy);
      const pulse = beat % 2 === 0 ? 1 : 0.55;
      glowL.intensity = (busy ? 55 : 26) * pulse * glow; glowR.intensity = (busy ? 55 : 26) * (1.55 - pulse) * glow;
      const c = PASTELS[beat % PASTELS.length];
      beams.forEach((b, i) => { const u = (b.material as THREE.ShaderMaterial).uniforms; u.col.value.copy(busy ? PASTELS[(beat + i * 3) % PASTELS.length] : new THREE.Color('#ffd9a0')); u.k.value = busy ? 0.34 : 0.18; });
      glowL.color.copy(c);
    },
    onMove: (b, k, show) => {
      chips(show, true);
      // the floor answers the move: a hop flashes it, a twirl rings out, a wiggle ripples
      pattern = ({ hop: 0, 'spin-hop': 0, twirl: 2, dance: 2, wiggle: 1, sway: 1, shimmy: 1, bow: 3, wave: 3 } as Record<string, number>)[b.move] ?? (k / 2) % 4;
      const troupe = onStage();
      if (['twirl', 'spin-hop', 'flip', 'cartwheel', 'jazz-hands', 'pose', 'heart'].includes(b.move)) for (const [slug] of show.spots) { const d = troupe.dancers.get(slug); if (d) fx.sparkles(troupe.at(d.life), ['#ffd36b', '#ff9bd2', '#9be8ff'][k % 3], 8, 0.4); }
      if (k === 0) for (const [slug] of show.spots) { const d = troupe.dancers.get(slug); if (d) fx.emoji(troupe.at(d.life).add(new THREE.Vector3(0, 0.7, 0)), MOVE_EMOJI[b.move] || '✨', 0.5); }
    },
    onFinale: (show) => {
      ballSpeed = 1.6; setTimeout(() => { ballSpeed = 0.35; }, 4000);
      const y = show.stage ? FLOOR + 3.2 : 3.2;
      for (const [x, z] of show.stage ? [[CX - 4.5, 2.5], [CX, 2.5], [CX + 4.5, 2.5]] : [[show.centre[0] - 2.5, show.centre[1]], [show.centre[0], show.centre[1] - 1], [show.centre[0] + 2.5, show.centre[1]]]) fx.confetti(new THREE.Vector3(x, y, z), 42, show.stage && z < STAGE.z1 ? FLOOR : 0);
      const troupe = onStage();
      for (const [slug] of show.watch) { const d = troupe.dancers.get(slug); if (d) fx.emoji(troupe.at(d.life).add(new THREE.Vector3(0, 0.6, 0)), '👏', 0.5); }
    },
  };
  const troupe = new Troupe(floor, ui, fx, hallOpts);
  scene.add(troupe.root);

  // ----- visitors: toy figures by the door, cheering
  const people = new THREE.Group(); scene.add(people);
  const fronts = new Map<string, { fig: THREE.Object3D; head: THREE.Vector3; el: HTMLElement; text: string; until: number; hop: number }>();
  let peopleKey = '';
  const placePeople = (vs: PlaceLive['visitors']) => {
    const key = vs.map((v) => v.id).join(',');
    if (key === peopleKey) return;
    peopleKey = key;
    for (const [id, f] of fronts) if (!vs.some((v) => v.id === id)) { people.remove(f.fig); f.el.remove(); fronts.delete(id); }
    vs.slice(0, 8).forEach((v, i) => {
      if (fronts.has(v.id)) return;
      const x = 14.0 + (i % 3) * 0.9, z = 11.2 + Math.floor(i / 3) * 0.8;
      const fig = at(toyFigure(v.look as any), x, 0, z, -2.3);
      fig.userData.visitor = v;
      finishShadows(fig); people.add(fig);
      const e = el('div', 'bubble visitor'); e.dataset.who = v.name; ui.appendChild(e);
      fronts.set(v.id, { fig, head: new THREE.Vector3(x, 2.05, z), el: e, text: '', until: 0, hop: 0 });
    });
    renderer.shadowMap.needsUpdate = true;
  };
  let chatSeen = Math.max(0, ...((live0?.chat || []).map((c) => c.at).filter((t) => (live0!.now - t) > 20000)));
  const hearChat = (l: PlaceLive) => {
    placePeople(l.visitors || []);
    for (const c of l.chat || []) {
      if (c.at <= chatSeen) continue;
      chatSeen = c.at;
      const f = fronts.get(c.visitor);
      if (f && walkerNow() && isMe({ id: c.visitor })) { sayOverMe(c.text); continue; }
      if (f) { f.text = c.text; f.until = performance.now() + 9000; }
    }
  };

  // ----- the page: plaque, signposts, marquee, clipboard
  // where you are: the shell's label, its live line from the marquee's words (plaqueSub);
  // the marquee itself is in the scene only, and what's on (the moves on the beat, who's
  // here, the bill, the playbill) is the dock's first tab
  const plaqueSub = el('span', '', 'on the commons');
  const mq = el('div', 'marquee');
  const mqTitle = el('small', '', 'Tonight'), mqLine = el('b', '', 'an open floor, crews welcome'), mqChips = el('div', 'pz-moves'), mqLoop = el('span', 'loop');
  const hud = el('div');
  const sNow = el('section'), sPlaybill = el('section');
  sNow.append(el('h4', '', 'On the stage'), mqLine, mqChips, mqLoop);
  sPlaybill.append(el('h4', '', 'Earlier on this stage'));
  const replays: Replays = new Replays({ ui, mq, billHost: sPlaybill, scene, fx, floor, opts: hallOpts, hall: troupe, people, onChange: () => { chips(onStage().show, true); if (latest) hearPage(latest); }, prewarm: (t) => { if (!still) t.prewarm(renderer, cam); } });
  let chipsKey = '';
  const chips = (show: Show | null, force = false) => {
    const k = show && !show.over ? show.key : '';
    if (k !== chipsKey || force) {
      if (k !== chipsKey) {
        chipsKey = k;
        mqChips.innerHTML = '';
        if (show && !show.over) show.s.beats.forEach((b) => mqChips.append(el('span', '', `${MOVE_EMOJI[b.move] || '✨'} ${b.move}${b.times && b.times > 1 ? ` ×${b.times}` : ''}`)));
      }
    }
    const cur = onStage().currentMove();
    [...mqChips.children].forEach((c, i) => c.classList.toggle('on', !!cur && cur.i === i));
    const lp = cur && show ? (cur.loop === 0 ? 'First time through' : cur.loop >= show.loops - 1 ? 'Last time' : ['Again!', 'Once more', 'And again'][(cur.loop - 1) % 3]) : show && !show.over && Date.now() < show.start ? 'Places, everyone…' : show && show.finale && !show.over ? 'Bravo!' : '';
    if (mqLoop.textContent !== lp) mqLoop.textContent = lp;
  };

  const sWho = el('section'), sOn = el('section'), sBill = el('section');
  hud.append(sNow, sWho, sOn, sBill, sPlaybill);
  let hudKey = '';
  const drawHud = (l: PlaceLive) => {
    const sh = troupe.show && !troupe.show.over ? troupe.show : null;
    const k = JSON.stringify([l.present.map((g) => [g.slug, g.crew?.name]), l.suggested, l.bill, sh?.key, l.visitors.length, replays.note()]);
    if (k === hudKey) return; hudKey = k;
    sWho.innerHTML = ''; sOn.innerHTML = ''; sBill.innerHTML = '';
    sWho.append(el('h4', '', `Here now · ${l.present.length}`));
    const ul = el('ul', 'who');
    for (const g of l.present) {
      const li = el('li'); const dot = el('i'); dot.style.background = g.color;
      li.append(dot, el('span', '', `${g.name} ${g.emoji || ''}`.trim()));
      if (g.crew) li.append(el('em', '', g.crew.name));
      li.append(el('small', '', replays.on ? 'in the wings' : sh?.spots.has(g.slug) ? 'dancing' : sh ? 'watching' : 'on the floor'));
      ul.append(li);
    }
    if (!l.present.length) ul.append(el('li', 'quiet', 'Nobody just now.'));
    sWho.append(ul);
    if (l.visitors.length) sWho.append(el('p', 'quiet', `Watching: ${l.visitors.map((v) => v.name).join(', ')}`));
    sOn.append(el('h4', '', 'On the floor'));
    sOn.hidden = !l.suggested && !replays.on && !sh;
    if (l.suggested) sOn.append(el('p', '', `${l.suggested.by_name} suggests ${l.suggested.title}. It starts when someone else agrees.`));
    if (replays.on) sOn.append(el('p', '', replays.note()));
    if (sh) sOn.append(el('p', '', `${sh.s.line}: ${sh.s.beats.map((b) => b.move).join(' · ')}.`));

    if (l.bill.length && !replays.past.length) {
      sBill.append(el('h4', '', 'The bill'));
      const bl = el('ul', 'bill');
      for (const b of l.bill.slice(0, 5)) { const li = el('li', '', `${b.line}`); bl.append(li); }
      sBill.append(bl);
    } else sBill.remove();
  };
  const hearPage = (l: PlaceLive) => {
    const sh = troupe.show && !troupe.show.over && l.showing && troupe.show.key === showKey(l.showing);
    const rp = replays.showing;
    // (P86) when it's quiet the board on the stage lists what was danced, so the marquee doesn't
    // repeat the last one: it says what the hall is now, an open floor
    const quiet = !rp && !sh && l.marquee.title !== 'Now dancing';
    const title = rp ? replays.title() : sh ? 'Now dancing' : quiet ? 'Tonight' : l.marquee.title;
    const open = l.tonight ? `${l.tonight.title.charAt(0).toUpperCase() + l.tonight.title.slice(1)}, by ${l.tonight.by_name}` : 'an open floor, crews welcome';
    const line = rp ? rp.line : quiet ? open : l.marquee.line;
    mqTitle.textContent = title; mqLine.textContent = line;
    setLine(title, line);
    plaqueSub.textContent = `${sh ? `Now dancing: ${line}` : rp ? `${title}: ${line}` : l.present.length ? `${l.present.length} here · ${title.toLowerCase()}: ${line}` : `Quiet just now · ${title.toLowerCase()}: ${line}`}`;
    mqTitle.textContent = title;
    (sNow.firstChild as HTMLElement).textContent = sh ? 'Now dancing' : rp ? title : 'On the stage';
    live = !!sh;
    if (!still) { shell().live(plaqueSub.textContent); hallPrimary(); shell().repeek(); }
    drawHud(l);
  };

  // the one primary action: Cheer while a dance is on, See the last dance when it's quiet, Back to now during a replay
  let live = false, primaryKey = '';
  // (P75) "See the last dance" is the brass plaque (lastDance below), not the top bar's tag: the
  // top bar keeps the way out while it's quiet
  function hallPrimary() {
    if (still) return;
    const k = replays.on ? 'stop' : live ? 'cheer' : replays.canReplay() ? 'replay' : 'out';
    drawLastDance(k);
    if (k === primaryKey) return;
    primaryKey = k;
    shell().primary(k === 'stop' ? { label: 'Back to now', onClick: () => replays.stop() }
      : k === 'cheer' ? { label: 'Cheer 👏', onClick: async () => { const t = talk ? await talk.cheer() : ''; if (t) shell().toast(t); } }
        : { label: '← The Commons', short: 'Commons', onClick: () => passage('/commons?look=40,23', 'Out to the Commons…') });
  }
  // (P75) the last dance, on a brass marquee plaque at the foot of the view: big, lit round its
  // edge, and there while walking and through your own eyes too. During a replay it says "Back
  // to now" while walking (when the top bar's "Stop walking" stands in front of that).
  let ldEl: HTMLButtonElement | null = null, ldKey = '';
  // (P75) and when nothing's dancing, the board of the last twenty over the stage (hallboard.ts)
  // (P86) it hangs on two chains from the fly bar, in front of the backdrop and the curtains' inner folds,
  // its foot just off the stage boards and its top under the valance
  const board = hallBoard({ ui, rect: { x0: CX - 4.8, x1: CX + 4.8, y0: FLOOR + 0.3, y1: FLOOR + 4.3, z: 0.75, chainTo: FLOOR + 4.98 }, play: (i) => replays.play(i) });
  function drawLastDance(k: string) {
    board.set(k === 'replay' && !still, replays.past, latest?.now ?? Date.now());
    if (!ldEl) {
      const css = document.createElement('style'); css.textContent = LAST_DANCE_CSS; document.head.appendChild(css);
      ldEl = el('button', 'pz-lastdance') as HTMLButtonElement;
      ldEl.type = 'button'; ldEl.dataset.pz = 'lastdance';
      ldEl.innerHTML = '<span class="bulbs" aria-hidden="true"></span><span class="in"><small></small><b></b><span class="sub"></span></span>';
      ldEl.addEventListener('click', () => { if (replays.on) replays.stop(); else replays.play(0); ldEl!.blur(); });
      // keys walk you: the plaque mustn't keep them
      ldEl.addEventListener('keydown', (e) => { if (!['Enter', ' '].includes(e.key)) ldEl!.blur(); });
      document.body.appendChild(ldEl);
    }
    const p = replays.past[0];
    const on = k === 'replay' && !!p, back = k === 'stop';
    const pw = p ? danceWhen(p.at) : null;
    const kick = back ? 'A replay is on' : pw ? `The last dance here · ${pw.date}, ${pw.time}` : '';
    const big = back ? 'Back to now' : '▶ See the last dance';
    const sub = back ? (replays.showing?.line || '') : p ? p.line : '';
    const key = JSON.stringify([on, back, kick, big, sub]);
    if (key === ldKey) return; ldKey = key;
    ldEl.hidden = !on && !back;
    ldEl.classList.toggle('back', back);
    ldEl.querySelector('small')!.textContent = kick;
    ldEl.querySelector('b')!.textContent = big;
    ldEl.querySelector('.sub')!.textContent = sub;
    ldEl.setAttribute('aria-label', back ? 'Back to now' : `See the last dance: ${sub}`);
  }
  let talk: Talk | null = null;
  // a cheer is seen at once: hands up by the door, hearts over the dancers
  function onCheered(j: any) {
    for (const f of fronts.values()) f.hop = 0.9;
    for (const d of troupe.list) { fx.emoji(troupe.at(d.life).add(new THREE.Vector3(0, 0.7, 0)), ['💗', '👏', '✨'][Math.floor(Math.random() * 3)], 0.5); }
    fx.confetti(new THREE.Vector3(14.9, 2.4, 11.4), 30, 0);
    if (j.ok) setTimeout(() => poll(), 300);
  }

  let latest = live0;
  // a card shows its own dance, not who's in the hall now
  if (live0 && !cardOf) { replays.hear(live0); troupe.hear(live0); hearChat(live0); hearPage(live0); }

  const dist = +(q.get('dist') || 41);
  const cam = makeCamera(vw, vh, target, camDir, dist);
  // a card's lens: the stage sharp from the marquee to the floor, the lights glowing without washing it out
  const composer: any = makeComposer(renderer, scene, cam, 'A', vw, vh, PR, cardOf ? CARD_LENS : { live: !still, bloom: 0.42, tiltBand: 0.3, tiltAmount: 2.2, vignette: 0.36 });
  // live, the page follows the window (fit.ts); stills keep their size
  const fit = still ? null : fitView({ renderer, composer, cam, pr: PR, onFit: (w, h) => { vw = w; vh = h; } });
  // the hour outside; after dark the room glows violet-warm and its lights come up
  const violet = new THREE.Color('#a3a6f2'), plum = new THREE.Color('#6a4058');
  const daylight = new Daylight(scene, renderer, { sun, hemi }, composer, cam, target, {
    fixedHour: fixedHourFrom(q) ?? (cardOf ? CARD_HOUR : still ? 19.4 : null),
    onApply: (s) => {
      const n = Math.max(s.lamps, s.windows);
      glow = 1 + 0.6 * n;
      hemi.color.lerp(violet, 0.35 + 0.35 * n); hemi.groundColor.lerp(plum, 0.35 + 0.35 * n); hemi.intensity = s.hemi + 0.5 * n;
      speckMat.opacity = 0.6 + 0.35 * n;
      stageL.intensity = 26 * glow;
      for (const p of pools) (p.material as THREE.MeshBasicMaterial).opacity = 0.14 + 0.14 * n;
    },
  });
  daylight.glowLamps(scene);
  daylight.apply(true);
  if (live0) daylight.setNow(live0.now);

  // ----- (a postcard from the Hall) the card's dance, held mid-move under the marquee: the resident
  // in front and a little bigger, whoever danced with it either side, any who watched standing apart at the
  // sides; then the card's paper over it, the view framed so the note keeps clear of them all
  function cardScene(c: HallCard) {
    // the card owns the page's clock while it poses them, as a film does (film.ts): the dance starts
    // on its own beat, and the frame comes out the same each time it's drawn
    let vt = 0, seed = 2166136261;
    for (const ch of `${cardOf}|${c.show.at}`) seed = Math.imul(seed ^ ch.charCodeAt(0), 16777619) >>> 0;
    const base = Math.floor(c.show.at / BEAT_MS) * BEAT_MS;
    Date.now = () => base + vt * 1000;
    performance.now = () => vt * 1000;
    Math.random = () => { seed = (seed + 0x6d2b79f5) >>> 0; let x = seed; x = Math.imul(x ^ (x >>> 15), x | 1); x ^= x + Math.imul(x ^ (x >>> 7), x | 61); return ((x ^ (x >>> 14)) >>> 0) / 4294967296; };
    const t = new Troupe(floor, ui, fx, { ...hallOpts, idle: undefined, onMove: undefined, onFinale: undefined });
    scene.add(t.root);
    const cast = [...c.dancers, ...c.watching].map((l) => ({ ...l, name: l.name || l.slug, color: l.color || '#f2a37b', glow: l.glow || '#9be8ff', why: 'visit', since: 0 }));
    t.hear({ now: Date.now(), place: 'hall', name: 'the Dance Hall', present: cast as any, showing: null, marquee: { title: 'Now dancing', line: c.show.line },
      bill: [], suggested: null, cheers: 0, chat: [], visitors: [] });
    // (closer together when there are watchers, so there's room for a gap between them and the dancers)
    const n = c.dancers.length, sp = c.watching.length ? 1.35 : Math.min(1.6, 9 / Math.max(1, n)); // clear of the speakers
    // on the stage: the resident at the front, the rest either side, a step back
    const dSpots: P[] = c.dancers.map((_, i) => { const k = Math.ceil(i / 2), side = i % 2 ? -1 : 1; return [CX + side * k * sp, i ? 2.2 - 0.08 * k : 3.2]; });
    // the watchers stand apart, one at each side, a clear gap from the dancers' line and a step upstage of it
    // (the server draws at most two, and only beside four dancers or fewer: plazmi.commons/on-card)
    const reach = (side: number) => Math.max(0, ...dSpots.map(([x]) => side * (x - CX)));
    const wSpots: P[] = c.watching.map((_, i) => { const side = i % 2 ? 1 : -1; return [CX + side * Math.min(4.6, Math.max(3.2, reach(side) + 2.0)), 1.6]; });
    const beats = c.show.beats?.length ? c.show.beats : [{ move: 'dance' }];
    const move = SNAP.find((m) => beats.some((b) => b.move === m)) || beats[0].move;
    c.dancers.forEach((l, i) => {
      const d = t.dancers.get(l.slug); if (!d) return;
      d.life.placeAt(dSpots[i], [0, 1]);
      if (i === 0) d.life.grow(CARD_ME);
      d.life.perform(move, 'dancing', [dSpots[i][0], dSpots[i][1] + 6]);
    });
    c.watching.forEach((l, i) => {
      const d = t.dancers.get(l.slug); if (!d) return;
      const p = wSpots[i];
      // a little smaller, standing still and nodding along, turned partly to the dancers and partly to us
      const face: P = [CX + (p[0] - CX) * 0.35, 5];
      d.life.placeAt(p, [face[0] - p[0], face[1] - p[1]]);
      d.life.grow(CARD_WATCHER);
      d.life.perform('nod', 'watching the dance', face);
    });
    // into the move (it starts on the next beat), where it reads in one frame
    const poseS = BEAT_MS / 1000 + 0.55;
    for (let k = 0; k < Math.round(poseS * 60); k++) { vt += 1 / 60; for (const d of t.dancers.values()) d.life.update(1 / 60); }
    // the hall as it is while a dance is on: the floor lit, the beams coloured, the marquee saying so
    setLine('Now dancing', c.show.line);
    hallOpts.onBeat?.(2, { start: 0 } as Show);
    throwSpecks(1.3);
    speckMat.opacity = 0.35; // fewer specks of light: the dancers come first
    // everyone in their own colour: the stage's light a soft fill from out front (close over the front of
    // the stage it blew the resident out to white), and the floor's coloured lights low and warm white
    stageL.position.set(CX, 5.5, 9); stageL.intensity *= 0.35;
    for (const l of [glowL, glowR]) { l.color.set('#fff1dc'); l.intensity *= 0.25; }
    // the paper first, so the frame knows where its note is
    const me = c.dancers[0];
    const paper = drawCard(ui, {
      headline: 'Come dance with us.', line: c.line, name: me?.name || cardOf || '', emoji: me?.emoji, colour: me?.color || '#f2a37b',
      frame: c.frame, designs: c.designs, day: c.postcard.day, address: c.postcard.address, host: c.postcard.host, where: 'the dance hall', night: true,
    });
    const pts: THREE.Vector3[] = [];
    const box = (x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) => {
      for (const x of [x0, x1]) for (const y of [y0, y1]) for (const z of [z0, z1]) pts.push(new THREE.Vector3(x, y, z));
    };
    c.dancers.forEach((_, i) => { const [x, z] = dSpots[i], s = i ? 1 : CARD_ME; box(x - 0.55 * s, FLOOR - 0.5, z - 0.45, x + 0.55 * s, FLOOR + 1.6 * s, z + 0.45); });
    c.watching.forEach((_, i) => { const [x, z] = wSpots[i]; box(x - 0.5, FLOOR - 0.5, z - 0.45, x + 0.5, FLOOR + 1.5, z + 0.45); });
    box(CX - 3.8, FLOOR + 5.05, 0.1, CX + 3.8, FLOOR + 6.2, 0.3); // the marquee
    // from the audience, a little to the right and just above the stage's edge: the rafter and its lights stay over the marquee
    const view = new THREE.Vector3(0.13, 0.06, 1).normalize();
    // the note on the right, under the stamp, as a postcard is laid out; the stage beside it (the
    // marquee clear of the stamp), or over it when that draws them larger
    paper.querySelector('.note')?.classList.add('right');
    const keepOut = noteRegion(paper, vw, vh);
    const region = { l: -0.86, r: 0.84, b: -0.84, t: 0.7 };
    const over = fitClear(18, vw / vh, view, pts, { ...region, t: 0.5 }, keepOut);
    const beside = fitPoints(18, vw / vh, view, pts, keepOut ? { ...region, r: keepOut.l - 0.02, t: 0.84 } : region);
    const f = beside.dist < over.dist ? beside : over;
    target.copy(f.target);
    cam.position.copy(f.target).addScaledVector(view, f.dist);
    cam.lookAt(f.target);
    cam.updateMatrixWorld();
    // in focus from the dancers up to the marquee; the sky above and the floor below soft, as a miniature
    const uvY = (x: number, y: number, z: number) => (new THREE.Vector3(x, y, z).project(cam).y + 1) / 2;
    const yd = uvY(CX, FLOOR + 0.7, dSpots[0]?.[1] ?? 2.5), ym = uvY(CX, FLOOR + 5.62, 0.2);
    for (const tp of composer.tilts || []) { tp.uniforms.focus.value = (yd + ym) / 2; tp.uniforms.band.value = Math.abs(ym - yd) / 2 + 0.05; }
    // each one's name under it, printed on the card, so the human can tell who's who (nametags.ts)
    t.pin(cam, vw, vh);
    for (const d of t.dancers.values()) { d.bubble.style.display = 'none'; d.tag.set({ look: 'card' }); }
    for (const l of c.watching) t.dancers.get(l.slug)?.tag.set({ watch: true, dim: ' · watching' });
    const mine = me && t.dancers.get(me.slug);
    if (mine) mine.tag.set({ me: me.color || '#f2a37b' });
    // a name that would touch the one beside it goes down a row, so every name shows
    nameTags.layout(cam, vw, vh, PR);
    const placed: { l: number; r: number; t: number; b: number }[] = [];
    for (const tag of [...t.dancers.values()].map((d) => d.tag).filter((x) => x.rect).sort((a, b) => a.rect!.x - b.rect!.x)) {
      for (let pass = 0; pass < 4; pass++) {
        const r = tag.rect!, l = r.x, rt = r.x + r.w, tp = r.y, bt = r.y + r.h;
        const hit = placed.find((p) => l < p.r + 4 && rt > p.l - 4 && tp < p.b + 2 && bt > p.t - 2);
        if (!hit) break;
        tag.dy += hit.b - tp + 3;
        nameTags.layout(cam, vw, vh, PR);
      }
      const r = tag.rect!;
      placed.push({ l: r.x, r: r.x + r.w, t: r.y, b: r.y + r.h });
    }
  }
  if (q.has('replay')) replays.play(+(q.get('replay') || 0));
  troupe.update(0); throwSpecks(0); lightTiles(0, false);
  if (hc) cardScene(hc);
  loadStage(3);
  // the graphics tier (quality.ts): set now, kept up as the frames come
  const gov = govern({ renderer, composer, scene, fit });
  watchFrom(cam);
  composer.render();
  // the loading scene lifts once frames come smoothly (quality.ts)
  if (still) done(); else gov.ready(done);
  if (still) { troupe.pin(cam, vw, vh); return; }
  homeSign(); guide('hall');
  moveTags(ui); // (P75) the move by each dancer
  {
    const sh = shell();
    sh.place('The Dance Hall', 'hall');
    sh.live(plaqueSub.textContent || '');
    onTab('on', 'What’s on', 10, () => ({ text: plaqueSub.textContent || '' })).appendChild(hud);
    waysToGo('/hall', { onClick: (href) => passage(href, href.startsWith('/commons') ? 'Out to the Commons…' : 'Off we go…') });
    hallPrimary();
  }
  troupe.prewarm(renderer, cam);

  // the way out, at the door: a sign that goes to the plaza just outside
  const outSign = doorSign({ scene, cam, dom: renderer.domElement, at: new THREE.Vector3((DOOR[0] + DOOR[1]) / 2, 1.7, D + 0.55), face: new THREE.Vector3(...camDir),
    text: '← Back to the Commons', near: '/commons?look=40,23', go: () => passage('/commons?look=40,23', 'Out to the Commons…') });
  const controls = new OrbitControls(cam, renderer.domElement);
  controls.target.copy(target);
  controls.enableDamping = true; controls.dampingFactor = 0.08;
  controls.minDistance = dist * 0.35; controls.maxDistance = dist * 1.5;
  controls.maxPolarAngle = Math.PI * 0.45;
  // (P84) a right-drag (two fingers) slides the view a little over the floor, never out of the hall:
  // a tall phone can bring the dancers up from under its bottom edge
  controls.enablePan = true; controls.screenSpacePanning = false; controls.panSpeed = 1;
  controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
  renderer.domElement.addEventListener('contextmenu', (e) => e.preventDefault());
  const PAN_BOX = new THREE.Box3(new THREE.Vector3(CX - 4, target.y, 3), new THREE.Vector3(CX + 4, target.y, D)), panWas = new THREE.Vector3();
  const keepInHall = () => { panWas.copy(controls.target); controls.target.clamp(PAN_BOX.min, PAN_BOX.max); cam.position.add(panWas.sub(controls.target).negate()); };
  soundPage({ place: 'hall', cam, focus: controls.target, daylight, crowds: () => [onStage() as any], surface: () => 'wood' });

  // ----- walking round as your own figure (walk.ts): onto the floor, up the steps onto the stage
  const walk = placeWalk({
    scene, cam, renderer, composer, ui, controls,
    bounds: { x0: -1.6, z0: -1.6, x1: W + 1.6, z1: D + 3.2 }, cell: 0.08, band: { base: 0, lo: -1, hi: 1.5 },
    grounds: [
      { x0: -1.4, z0: -1.4, x1: W + 1.4, z1: D + 3.0, y: 0 },
      { x0: STAGE.x0, z0: STAGE.z0, x1: STAGE.x1, z1: STAGE.z1, y: FLOOR }, { x0: CX - 1, z0: STAGE.z1, x1: CX + 1, z1: STAGE.z1 + 0.55, y: FLOOR / 2 },
    ],
    sources: () => [hall, tiles, people],
    start: () => ({ x: CX, z: D - 1.2, yaw: Math.PI }),
    // whoever's on the floor: the hall's own plazmi, or a replay's cast
    rounds: () => { const t = onStage(); return t.list.filter((d) => d.life.root.visible).map((d) => ({ x: d.life.spot[0] + t.root.position.x, z: d.life.spot[1] + t.root.position.z, r: 0.3, level: 0 })); },
    glancers: () => { const t = onStage(); return t.list.map((d) => ({ root: d.life.root, at: () => [d.life.spot[0] + t.root.position.x, d.life.spot[1] + t.root.position.z], level: () => 0,
      hello: () => fx.emoji(t.at(d.life).add(new THREE.Vector3(0, 0.6, 0)), '👋', 0.4, 1.8) })); },
    // out through the doorway: back on the Commons, by the hall's doors
    triggers: [{ rect: { x0: DOOR[0], z0: D + 1.6, x1: DOOR[1], z1: D + 3.2 }, band: 0, go: (view) => { passage(`/commons?walk=1&from=hall&view=${view}`, 'Out to the Commons…'); } }],
  });
  // (P108) the whole camera, as on the maps (camctl.ts): the keys (WASD, Q/E, +/−, R) and the brass dial
  // too; by touch one finger turns, two drag, pinch and twist
  const home0 = { target: target.clone(), dist, dir: new THREE.Vector3(...camDir) };
  const look = camControls({ controls, cam, dom: renderer.domElement, home: () => home0, bounds: () => PAN_BOX,
    active: () => !walk.walker() && !walk.busy() && controls.enabled, shown: () => !walk.walker(), oneFinger: () => 'turn' });

  // ----- the loop, adapting quality to stay smooth
  let last = performance.now(), winStart = last, winFrames = 0, winSum = 0, calmFor = 0;
  stats.pr = PR;
  // (P68) following a plazmi: the camera keeps it in frame on the floor
  const fol = followPage({
    cam, controls, near: dist * 0.6, far: dist * 0.9, paused: () => !!walk.walker() || walk.busy(),
    find: (s) => { const t = onStage(), d = t.dancers.get(s); return d && !d.leaving ? t.at(d.life) : null; },
  });
  const loop = () => {
    const now = performance.now();
    // Battery saver keeps to its frame rate; the governor watches the rest (quality.ts)
    if (!gov.frame(now)) { requestAnimationFrame(loop); return; }
    const dt = Math.min((now - last) / 1000, 0.05);
    winSum += now - last; winFrames++; last = now;
    if (now - winStart > 1000) { stats.fps = Math.round(1000 * winFrames / winSum); PR = gov.pr; stats.pr = PR; winStart = now; winSum = 0; winFrames = 0; }
    daylight.update(dt);
    // during a replay the hall's own plazmi wait in the wings
    if (replays.on) replays.update(dt); else troupe.update(dt);
    fx.update(dt);
    throwSpecks(dt);
    chips(onStage().show);
    const walkingMe = !!walkerNow();
    for (const [id, f] of fronts) {
      if (f.hop > 0) { f.hop -= dt; f.fig.position.y = Math.max(0, Math.sin((1 - f.hop / 0.9) * Math.PI * 3)) * 0.25; } else f.fig.position.y = 0;
      // (P70) walking, you're the walker: your own figure by the door is left out (and out of your way)
      const mine = isMe({ id }), vis = !(mine && walkingMe);
      f.fig.userData.walkSkip = mine;
      if (f.fig.visible !== vis) { f.fig.visible = vis; renderer.shadowMap.needsUpdate = true; }
      const on = now < f.until && !replays.on && vis;
      f.el.style.display = on ? 'block' : 'none';
      if (on) { if (f.el.textContent !== f.text) f.el.textContent = f.text; const p = f.head.clone().project(cam); f.el.style.left = `${((p.x + 1) / 2) * vw}px`; f.el.style.top = `${((1 - p.y) / 2) * vh}px`; }
    }
    const wk = walk.walker();
    if (wk) wk.update(dt); else if (!walk.busy()) { fol.tick(dt); look.tick(dt); controls.update(); look.after(); keepInHall(); }
    // (P75) the board over the stage; the plaque stands in for it when the stage is out of view.
    // The door's sign is the way out while walking; from above it would stand over the floor
    board.update(cam, vw, vh);
    ldEl?.classList.toggle('by-board', board.inView());
    if (outSign.visible !== !!wk) outSign.visible = !!wk;
    renderer.info.reset();
    composer.render();
    stats.calls = renderer.info.render.calls; stats.tris = renderer.info.render.triangles;
    if (replays.on) replays.pin(cam, vw, vh); else troupe.pin(cam, vw, vh);
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  talk = new Talk(shell(), {
    kind: 'place', chatOrder: 20, cheer: true,
    quiet: 'Quiet so far. Words, songs and dances here show up as they happen.',
    send: async (text) => {
      const j = await fetch('/api/place/hall/chat', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text }) }).then((r) => r.json()).catch(() => ({ ok: false }));
      return { ok: !!j.ok, error: j.error };
    },
    onCheer: async () => {
      const j = await fetch('/api/place/hall/cheer', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' }).then((r) => r.json()).catch(() => ({ ok: false }));
      onCheered(j);
      return j;
    },
  });
  talk.bind('hall', () => latest?.now || Date.now());
  const poll = async () => {
    try {
      const l: PlaceLive | null = await fetch('/api/place/hall/live').then((r) => (r.ok ? r.json() : null));
      if (!l) return;
      latest = l;
      daylight.setNow(l.now);
      replays.hear(l); if (!replays.on) troupe.hear(l);
      hearChat(l); hearPage(l); talk.refresh();
    } catch { /* the next poll will do */ }
  };
  setInterval(poll, POLL_MS);
}

arrival(() => (window as any).__ready === true);
main().catch((e) => { console.error(e); done(); });

// (P75) the last dance's plaque: brass, engraved, a row of warm bulbs top and bottom like the
// marquee's. Bottom centre (beside the dock on wider screens), above the dock's peek on a
// phone, and above the walking wheel and the say-something disc while walking on a phone.
const LAST_DANCE_CSS = `
.pz-lastdance{position:fixed;z-index:20;left:max(50%, calc(var(--pz-g) + 360px + 16px + 190px));bottom:calc(var(--pz-g) + 4px);transform:translateX(-50%);
  width:380px;max-width:calc(100vw - 2 * var(--pz-g));padding:15px 18px 14px;border:2px solid var(--pz-ink,#3a2a20);border-radius:4px;cursor:pointer;
  font:inherit;font-family:ui-rounded,'SF Pro Rounded','Nunito',system-ui,sans-serif;color:var(--pz-brass-ink,#4a3712);text-align:center;
  background:linear-gradient(180deg,var(--pz-brass-2,#e3c473) 0%,var(--pz-brass,#c9a24a) 62%,#b38a36 100%);
  box-shadow:inset 0 0 0 3px rgba(255,240,200,.55),inset 0 0 0 5px rgba(74,55,18,.35),0 12px 26px -12px rgba(40,26,14,.7);transition:transform .16s ease-out,box-shadow .16s ease-out}
.pz-lastdance[hidden],.pz-lastdance.by-board{display:none}
.pz-lastdance:hover{background:linear-gradient(180deg,#efd68e 0%,var(--pz-brass-2,#e3c473) 62%,#c49a43 100%)}
.pz-lastdance:active{transform:translateX(-50%) translateY(1px);box-shadow:inset 0 0 0 3px rgba(255,240,200,.55),inset 0 0 0 5px rgba(74,55,18,.35)}
.pz-lastdance:focus-visible{outline:2px solid var(--pz-brass,#c9a24a);outline-offset:3px}
.pz-lastdance .in{display:flex;flex-direction:column;align-items:center;gap:1px}
.pz-lastdance small{font-size:12px;font-weight:800;color:rgba(74,55,18,.8)}
.pz-lastdance b{font-size:23px;font-weight:800;line-height:1.15;letter-spacing:.005em;text-shadow:0 1px 0 rgba(255,244,214,.6)}
.pz-lastdance .sub{font-size:13.5px;font-weight:600;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#5a4418}
.pz-lastdance .sub:empty{display:none}
.pz-lastdance .bulbs:before,.pz-lastdance .bulbs:after{content:"";position:absolute;left:10px;right:10px;height:6px;
  background:radial-gradient(circle,#fff6d8 0 1.6px,#ffd36b 2.2px,rgba(255,211,107,0) 3px) 0 0/14px 6px repeat-x}
.pz-lastdance .bulbs:before{top:4px}.pz-lastdance .bulbs:after{bottom:4px}
.pz-lastdance.back{width:260px;padding:12px 16px 11px}
.pz-lastdance.back b{font-size:19px}
html:not(.pz-walking) .pz-lastdance.back{display:none}
html.pz-drawer-open .pz-lastdance,html.pz-coaching .pz-lastdance{visibility:hidden}
@media (max-width:600px){
  .pz-lastdance{left:50%;bottom:calc(var(--pz-dock-h) + 12px);width:min(330px, calc(100vw - 2 * var(--pz-g)));padding:12px 14px 11px}
  .pz-lastdance b{font-size:21px}
  html.pz-walking.pz-touch .pz-lastdance{bottom:212px}
}
@media (max-height:480px){
  .pz-lastdance{left:50%;bottom:var(--pz-g);width:300px;padding:10px 14px 9px}
  .pz-lastdance b{font-size:18px}.pz-lastdance small{display:none}
  html.pz-walking.pz-touch .pz-lastdance{bottom:var(--pz-g)}
}
`;
